// Keyboard + mouse device: owns all DOM listeners (pointer, wheel, keys,
// focus hygiene). Wheel zoom mutates camera.zoom directly — event-driven on
// purpose, so zooming keeps working while menus freeze the sim.

import type { Camera } from '../camera'
import type { Binding, DeviceSnapshot } from './actions'
import { compositeKeyVector, type Vec2 } from './deadzone'

/** Accumulated pointer travel (px) that counts as "the mouse is in use" — a
 * desk bump must not flip aim source or UI glyphs. */
const MOTION_ACTIVITY_PX = 24

export interface KeyboardMouseDevice extends DeviceSnapshot {
  /** Canvas-relative screen position + primary-button state (composer converts to world). */
  readonly mouseScreen: { x: number; y: number; down: boolean }
  /** Mouse-only activity (motion past threshold, buttons, wheel) — never keys.
   *  Feeds aim source selection (systems/aim.ts); the combined `activityAt`
   *  above still drives lastDevice. */
  readonly mouseActivityAt: number
  clearOnBlur(): void
}

export function createKeyboardMouseDevice(canvas: HTMLCanvasElement, camera: Camera): KeyboardMouseDevice {
  const mouseScreen = { x: 0, y: 0, down: false }
  const keysDown = new Set<string>()
  const keyPressedAt = new Map<string, number>()
  const mouseDown = new Set<number>()
  const mousePressedAt = new Map<number, number>()
  let activityAt = -Infinity
  let mouseActivityAt = -Infinity
  let motionAccum = 0

  const bumpActivity = (): void => {
    activityAt = performance.now()
  }

  const bumpMouseActivity = (): void => {
    const now = performance.now()
    activityAt = now
    mouseActivityAt = now
    motionAccum = 0
  }

  canvas.addEventListener('pointermove', e => {
    const rect = canvas.getBoundingClientRect()
    mouseScreen.x = e.clientX - rect.left
    mouseScreen.y = e.clientY - rect.top
    motionAccum += Math.abs(e.movementX) + Math.abs(e.movementY)
    if (motionAccum >= MOTION_ACTIVITY_PX) bumpMouseActivity()
  })
  canvas.addEventListener('pointerdown', e => {
    mouseDown.add(e.button)
    mousePressedAt.set(e.button, performance.now())
    if (e.button === 0) mouseScreen.down = true
    canvas.setPointerCapture(e.pointerId)
    bumpMouseActivity()
  })
  canvas.addEventListener('pointerup', e => {
    mouseDown.delete(e.button)
    if (e.button === 0) mouseScreen.down = false
    canvas.releasePointerCapture(e.pointerId)
  })

  // The game owns both mouse buttons (lash now, hook later) — never the
  // browser context menu.
  canvas.addEventListener('contextmenu', e => e.preventDefault())

  canvas.addEventListener('wheel', e => {
    e.preventDefault()
    const factor = Math.exp(-e.deltaY * 0.001)
    camera.zoom = camera.zoom * factor
    bumpMouseActivity()
  }, { passive: false })

  window.addEventListener('keydown', e => {
    if (e.repeat) return
    keysDown.add(e.code)
    keyPressedAt.set(e.code, performance.now())
    bumpActivity()
  })
  window.addEventListener('keyup', e => keysDown.delete(e.code))

  const anyDown = (codes: string[]): boolean => codes.some(c => keysDown.has(c))

  return {
    kind: 'kbm',
    mouseScreen,
    get activityAt() {
      return activityAt
    },
    get mouseActivityAt() {
      return mouseActivityAt
    },
    isDown(b: Binding): boolean {
      if ('key' in b) return keysDown.has(b.key)
      if ('mouseButton' in b) return mouseDown.has(b.mouseButton)
      return false
    },
    pressedAt(b: Binding): number {
      if ('key' in b) return keyPressedAt.get(b.key) ?? -Infinity
      if ('mouseButton' in b) return mousePressedAt.get(b.mouseButton) ?? -Infinity
      return -Infinity
    },
    vector(b: Binding): Vec2 | null {
      if (!('keyVector' in b)) return null
      const k = b.keyVector
      return compositeKeyVector(anyDown(k.up), anyDown(k.down), anyDown(k.left), anyDown(k.right))
    },
    clearOnBlur() {
      keysDown.clear()
      mouseDown.clear()
      mouseScreen.down = false
    },
  }
}
